'use client';

import React, { useEffect } from 'react';
import Script from 'next/script';
import { consentSignals, noConsent } from '@/lib/consent';
import { clearCookies, GA_COOKIES } from './trackerCookies';
import AnalyticsPageViews from './AnalyticsPageViews';
import { useConsent } from './useConsent';

/**
 * Loads Google Analytics, and only once someone has said it may.
 *
 * This is the strict reading of Article 5(3) of the ePrivacy Directive and of
 * Article 99 of the Lithuanian Elektroninių ryšių įstatymas, and it is
 * deliberate. Google's own Consent Mode offers a looser arrangement where the
 * tag loads immediately and merely withholds its cookies — but the tag still
 * contacts Google and still discloses the visitor's IP address, and whether
 * that is lawful without consent is exactly the argument this site does not
 * need to have. Until the answer is yes, no request is made at all.
 *
 * The consent signals are still declared, because they govern what Google may
 * do with the data once it does arrive.
 */
export default function GoogleAnalytics({ tagId }: { tagId: string }) {
  const { choice } = useConsent();
  const granted = choice?.analytics === true;

  useEffect(() => {
    /* Not asked yet, or agreed — nothing to undo. */
    if (!choice || choice.analytics) return;
    /* Refused, or agreed once and withdrawn. The second case is the one that
       matters: the tag is already in this page and will keep writing cookies
       until it is told otherwise, so tell it, then remove what it wrote. */
    window.gtag?.('consent', 'update', { analytics_storage: 'denied' });
    clearCookies(GA_COOKIES);
  }, [choice]);

  if (!granted) return null;

  /* The signals describe every purpose, not just this one — a visitor who
     agreed to analytics and refused marketing must reach Google as exactly
     that, rather than as a blanket yes from the tag that happens to be
     loading. */
  const signals = JSON.stringify(consentSignals(choice ?? noConsent()));

  return (
    <>
      <Script
        src={`https://www.googletagmanager.com/gtag/js?id=${tagId}`}
        strategy="afterInteractive"
      />
      <Script id="ga-init" strategy="afterInteractive">
        {`window.dataLayer=window.dataLayer||[];function gtag(){dataLayer.push(arguments);}` +
          `window.gtag=gtag;gtag('consent','default',${signals});` +
          `gtag('js',new Date());gtag('config','${tagId}');`}
      </Script>
      <AnalyticsPageViews tagId={tagId} />
    </>
  );
}
